import { useState, useRef, useEffect, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import { useApolloClient, gql } from '@apollo/client';
import { GifPicker } from 'gif-picker-react';
import { Klipy } from 'gif-picker-react/providers/klipy';
import CustomGifPicker from './CustomGifPicker.jsx';
import EmojiPicker from 'emoji-picker-react';
import EmployeePicker from 'src/RnRCommonComponent/EmployeePicker/index.js';
import { Button, Modal, Spin, Tooltip, Typography, message } from 'antd';
import {
	DeleteOutlined,
	CloseOutlined,
	CloseCircleFilled,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import dompurify from 'dompurify';
import { motion } from 'motion/react';
import RewardPointsAndBonusContainer from './../../container/rewardPointsAndBonusContainer/RewardPointsAndBonusContainer.jsx';
import SendRewardContainer from './../../container/sendRewardContainer/SendRewardContainer.jsx';
import RecentActivityContainer from './../../container/recentActivityContainer/RecentActivityContainer.jsx';
import Feed from 'src/home/presentation/feed/Feed.jsx';
import RewardDashboardContainerImport from './../../container/rewardDashboardContainer/RewardDashboardContainer.jsx';
import RewardsLeaderBoardContainer from './../../container/rewardsLeaderBoardContainer/RewardsLeaderBoardContainer.jsx';
import emoteIcon from './../../../assets/svg/emote.svg';
import { ReactComponent as RewardIcon } from './../../../assets/svg/rewardOne.svg';
import { ReactComponent as RecipientIcon } from './../../../assets/svg/recipientOne.svg';
import send from './../../../assets/svg/send.svg';
import send_gray from './../../../assets/svg/send_gray.svg';
import gifIcon from './../../../assets/svg/gif.svg';
import './style.scss';
import RewardsProgressContainer from 'src/home/container/rewardProgressContainer';
import RecognitionAnnouncements from 'src/home/presentation/dashboardPresentation/RecognitionAnnouncements';
import AccountClaimPicker from 'src/RnRCommonComponent/EmployeePicker/AccountClaimPickerComponent';
import useMyBadgeAssignments from 'src/my-rewards/hooks/useMyBadgeAssignments.js';
import { TextAreaWithTags } from 'src/home/presentation/dashboardPresentation/TextAreaWithTags.jsx';

const { Text } = Typography;
const { confirm } = Modal;
const sanitizer = dompurify.sanitize;

const klipyProvider = Klipy(process.env.REACT_APP_GIF_KLIPY_KEY, {
	contentFilter: 'high',
});

const RewardDashboardContainer = memo(RewardDashboardContainerImport);

const DashboardPresentation = (props) => {
	const {
		sendRewardObject,
		setSendRewardObject,
		fullAccountClaim,
		setFullAccountClaim,
		handleHasSeenPopup,
		isOpenRewardModel,
		setIsOpenRewardModel,
		isShoutOutSend,
		handleSendShoutout,
		isEmployeeSelected,
		setIsEmployeeSelected,
		recipients,
		setRecipients,
		handleReplySubmit,
		handleSetToPrivate,
		handleClapClick,
		newestFeedData,
		topFeedData,
		teamFeedData,
		milestonesFeedData,
		newestFeedLoading,
		topFeedLoading,
		teamFeedLoading,
		milestonesFeedLoading,
		hasMoreNewestFeedData,
		hasMoreTopFeedData,
		hasMoreTeamFeedData,
		hasMoreMilestonesFeedData,
		isLoadingMoreNewestFeed,
		isLoadingMoreTopFeed,
		isLoadingMoreTeamFeed,
		isLoadingMoreMilestonesFeed,
		loadMoreNewestFeed,
		loadMoreTopFeed,
		loadMoreTeamFeed,
		loadMoreMilestonesFeed,
		tagsFeed,
		selectedTags,
		setSelectedTags,
		recognitionPointsTotal,
		recognitionPointsLeaderboard,
		recognitionPointsBySubCompanyLeaderboard,
		userStatsLoading,
	} = props;

	const client = useApolloClient();
	const [accountClaimId, setAccountClaimId] = useState([]);
	const [isGifPickerVisible, setIsGifPickerVisible] = useState(false);
	const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);
	const [cursorPosition, setCursorPosition] = useState(0);
	const [selectedEmployee, setSelectedEmployee] = useState(null);
	const [searchedEmployeeData, setSearchedEmployeeData] = useState([]);
	const [isHovered, setIsHovered] = useState('');
	const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);
	const [shouldFocusTextArea, setShouldFocusTextArea] = useState(false);
	const currentUser = useSelector((state) => state?.user?.currentUser);
	const { company } = currentUser;
	const customSendRecognitionBoxText =
		company?.recognitionSettings?.customSendRecognitionBoxText?.length > 0
			? company.recognitionSettings.customSendRecognitionBoxText
			: null;
	const gifIds = company?.gifIds;
	const customModal = company?.recognitionSettings?.customModal;
	const allowRecognitionsForAccountClaims =
		company?.recognitionSettings?.allowRecognitionsForAccountClaims || false;
	const requireBadgeAssignmentMessage =
		company?.recognitionSettings?.requireBadgeAssignmentMessage || false;
	const { badgeList, isLoading, error, assignmentsSent } =
		useMyBadgeAssignments(currentUser, false, () => {});
	const prevRecipientsLengthRef = useRef(recipients.length);
	const availableTags = company?.recognitionSettings?.recognitionHashtags || [];

	const isSharingVisible = useMemo(
		() => !company?.recognitionSettings?.hideRecognitionSocialShare,
		[company?.recognitionSettings?.hideRecognitionSocialShare]
	);

	useEffect(() => {
		const hasSeenRecognitionPopup = localStorage.getItem(
			'hasSeenRecognitionPopup'
		);

		if (
			!hasSeenRecognitionPopup &&
			customModal?.isEnabled === true &&
			(customModal?.title || customModal?.content) &&
			(!currentUser?.recognitionEvents?.hasSeenPopup ||
				customModal?.showEachLogin === true)
		) {
			setIsCustomModalVisible(true);
		}
	}, [customModal, currentUser?.recognitionEvents?.hasSeenPopup]);

	useEffect(() => {
		if (
			allowRecognitionsForAccountClaims &&
			accountClaimId?.length > 0 &&
			fullAccountClaim?.length > 0
		) {
			const userIds = [];
			const accountClaimIds = [];

			fullAccountClaim.forEach((claimWrapper) => {
				const claim = claimWrapper.claim;
				if (claim.userId) {
					userIds.push(claim.userId);
				} else {
					accountClaimIds.push(claim.id);
				}
			});

			setSendRewardObject((prev) => ({
				...prev,
				employeeDetail:
					userIds.length > 0
						? userIds[0]
						: accountClaimIds.length > 0
							? 'account_claim_selected'
							: '',
				userIds: userIds,
				accountClaimIds: accountClaimIds,
			}));
		} else if (
			allowRecognitionsForAccountClaims &&
			accountClaimId?.length === 0
		) {
			setSendRewardObject((prev) => ({
				...prev,
				employeeDetail: null,
				userIds: [],
				accountClaimIds: [],
			}));
		}
	}, [allowRecognitionsForAccountClaims, accountClaimId, fullAccountClaim]);

	useEffect(() => {
		const currentRecipientsLength = recipients.length;
		const prevRecipientsLength = prevRecipientsLengthRef.current;

		if (
			sendRewardObject?.selectedBadge?.id &&
			currentRecipientsLength > prevRecipientsLength
		) {
			checkAllEligibility();
		}

		prevRecipientsLengthRef.current = currentRecipientsLength;
	}, [recipients.length, sendRewardObject?.selectedBadge?.id]);

	const GET_BADGE_ASSIGNMENT_ELIGIBILITY = gql`
		query GetBadgeAssignmentEligibility(
			$userId: ID
			$accountClaimId: ID
			$badgeId: ID!
		) {
			getBadgeAssignmentEligibility(
				userId: $userId
				accountClaimId: $accountClaimId
				badgeId: $badgeId
			)
		}
	`;

	const checkAssignmentEligibility = async ({
		userId,
		accountClaimId,
		badgeId,
	}) => {
		const variables = {
			badgeId: badgeId,
		};
		if (userId) {
			variables.userId = userId;
		}
		if (accountClaimId) {
			variables.accountClaimId = accountClaimId;
		}
		const { data } = await client.query({
			query: GET_BADGE_ASSIGNMENT_ELIGIBILITY,
			variables: variables,
			fetchPolicy: 'network-only',
		});
		const isEligible = data?.getBadgeAssignmentEligibility || false;
		return isEligible;
	};

	const showChooseRemoveOption = ({
		newRecipients = [],
		newAccountClaimId = [],
		totalIneligible,
	}) => {
		const isMultiple = totalIneligible > 1;
		confirm({
			title: `Alert`,
			icon: <ExclamationCircleOutlined />,
			content: `${totalIneligible + ' recipient' + (isMultiple ? 's are' : ' is')} ineligible for the selected Recognition. Would you like to remove the ineligible recipient${isMultiple ? 's' : ''} or select another Recognition?`,
			okText: 'Select Another',
			cancelText: `Remove Ineligible`,
			centered: true,
			onOk() {
				setSendRewardObject({
					...sendRewardObject,
					selectedBadge: null,
					rewardtype: null,
				});
			},
			onCancel() {
				setSelectedEmployee(newRecipients[0]?.userId || null);
				setSendRewardObject({
					...sendRewardObject,
					employeeDetail: newRecipients[0]?.userId || 'account_claim',
				});
				setAccountClaimId(newAccountClaimId);
				setRecipients(newRecipients);
			},
		});
	};

	const checkAllEligibility = async () => {
		if (sendRewardObject.selectedBadge?.id) {
			const eligibleRecipients = [];
			for (const recipient of recipients) {
				const isEligible = await checkAssignmentEligibility({
					userId: recipient.userId,
					accountClaimId: recipient.accountClaimId,
					badgeId: sendRewardObject.selectedBadge.id,
				});
				if (isEligible) {
					eligibleRecipients.push(recipient);
				}
			}
			if (eligibleRecipients.length === 0) {
				setSendRewardObject({
					...sendRewardObject,
					selectedBadge: null,
					rewardtype: null,
				});
				message.info(
					'All recipients have reached their limit for the selected Recognition. Please select a different Recognition.'
				);
			} else if (eligibleRecipients.length !== recipients.length) {
				const newAccountClaimId = [];
				const totalIneligible = recipients.length - eligibleRecipients.length;
				if (accountClaimId) {
					accountClaimId.forEach((recValue) => {
						if (
							eligibleRecipients.some(
								(rec) => rec.accountClaimId === recValue.value
							)
						) {
							newAccountClaimId.push(recValue);
						}
					});
				}
				showChooseRemoveOption({
					newRecipients: eligibleRecipients,
					newAccountClaimId: newAccountClaimId,
					totalIneligible: totalIneligible,
				});
			}
		}
	};

	const handleMouseOver = (elementType) => {
		setIsHovered(elementType);
	};

	const handleMouseLeave = () => {
		setIsHovered('');
	};

	const handleCustomModalClose = () => {
		setIsCustomModalVisible(false);
		localStorage.setItem('hasSeenRecognitionPopup', 'true');

		if (!currentUser?.recognitionEvents?.hasSeenPopup && handleHasSeenPopup) {
			handleHasSeenPopup();
		}
	};

	const handleOnGifClicked = (img) => {
		setSendRewardObject({
			...sendRewardObject,
			content: {
				...sendRewardObject.content,
				gifURL: img.imageUrl,
			},
		});
		setIsGifPickerVisible(false);
	};

	const handleOnEmojiClicked = (img) => {
		const content = sendRewardObject?.content || {};
		const message = content?.message || '';

		const newCursorPosition = cursorPosition + img.emoji.length;

		const newText =
			message === ''
				? img.emoji
				: message.substring(0, cursorPosition) +
					img.emoji +
					message.substring(cursorPosition);

		setSendRewardObject({
			...sendRewardObject,
			content: {
				...content,
				message: newText,
			},
		});

		setCursorPosition(newCursorPosition);
		setShouldFocusTextArea(true);
		setIsEmojiPickerVisible(false);
	};

	const handleDeleteGif = () => {
		setSendRewardObject({
			...sendRewardObject,
			content: {
				...sendRewardObject.content,
				gifURL: null,
			},
		});
	};

	const {
		employeeDetail,
		content,
		rewardtype,
		userIds = [],
		accountClaimIds = [],
	} = sendRewardObject || {};
	const isEmployeeDetailValid =
		employeeDetail ||
		accountClaimId?.length > 0 ||
		userIds?.length > 0 ||
		accountClaimIds?.length > 0;
	const isContentValid = (() => {
		if (!content) return false;

		const hasMessage = content.message && content.message.trim().length !== 0;
		const hasGif = content.gifURL;

		if (requireBadgeAssignmentMessage) {
			return hasMessage;
		}

		return hasMessage || hasGif;
	})();
	const matchedEmployee = searchedEmployeeData.find(
		(e) => e.id === sendRewardObject?.employeeDetail
	);

	return (
		<div>
			<div className="dashboard-grid custom-form-group">
				<div className="card-scroll" style={{ overflow: 'visible' }}>
					<div className="">
						<div
							className="d-card height-equal mb-3"
							style={{ overflow: 'visible' }}
						>
							<div className="d-card-head">
								<h4 className="dashboard-title">Send Recognition</h4>
							</div>
							{customSendRecognitionBoxText && (
								<motion.div
									style={{ color: 'rgba(0, 0, 0, 0.65)' }}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.5, ease: 'easeOut' }}
								>
									<div
										dangerouslySetInnerHTML={{
											__html: sanitizer(customSendRecognitionBoxText, {
												ADD_ATTR: ['target', 'rel'],
											}),
										}}
									/>
								</motion.div>
							)}
							<div className="w-100 send-modal-btn">
								<div className="send-modal-container">
									<div
										style={{
											borderRadius: 24,
											color: 'var(--primary-btn-color, var(--theme-primary))',
											fontSize: '14px',
											fontWeight: 700,
											border: 'none',
										}}
									>
										{false ? (
											<div
												className="dropdown-wrapper"
												onMouseOver={() => handleMouseOver('recipient')}
												onMouseLeave={handleMouseLeave}
												onClick={() => {
													setSelectedEmployee(null);
													setSendRewardObject({
														...sendRewardObject,
														employeeDetail: null,
													});
												}}
												style={
													isHovered === 'recipient' ? { cursor: 'pointer' } : {}
												}
											>
												<div className="badge-box">
													<div className="dropdown-container">
														<div
															className={`dropdown-content ${isHovered === 'recipient' ? 'hidden' : ''}`}
														>
															<RecipientIcon className="at-symbol-icon-filled" />
															<div className="dropdown-text">
																<Text
																	style={{ maxWidth: 120, color: '#fff' }}
																	ellipsis={true}
																>
																	{(
																		matchedEmployee?.firstName +
																		' ' +
																		matchedEmployee?.lastName
																	)?.trim()}
																</Text>
															</div>
														</div>
														<div
															className={`dropdown-content hover-content ${isHovered === 'recipient' ? 'visible' : ''}`}
														>
															<CloseCircleFilled
																style={{
																	fontSize: '18px',
																	paddingRight: '0px',
																}}
															/>
															<span className="remove-text">
																<Text
																	style={{ maxWidth: 120, color: '#fff' }}
																	ellipsis={true}
																>
																	Remove
																</Text>
															</span>
														</div>
													</div>
												</div>
											</div>
										) : (
											<>
												{allowRecognitionsForAccountClaims ? (
													<AccountClaimPicker
														value={accountClaimId}
														onChange={(newAcc) => {
															setAccountClaimId(newAcc);
															setIsEmployeeSelected(false);
														}}
														companyId={currentUser?.companyId}
														fullValue={fullAccountClaim}
														setFullValue={(selectionArray) => {
															const mappedRecipients = selectionArray.map(
																(selection) => {
																	const recipient = {
																		accountClaimId: selection.claim.id,
																	};
																	if (selection.claim?.userId) {
																		recipient.userId = selection.claim.userId;
																	}
																	return recipient;
																}
															);
															setRecipients(mappedRecipients);
															setFullAccountClaim(selectionArray);
														}}
														style={{
															borderRadius: 24,
															color:
																'var(--primary-btn-color, var(--theme-primary))',
															fontSize: '14px',
															fontWeight: 700,
															border:
																'1px solid var(--primary-btn-color, var(--theme-primary))',
															width: '165px',
															background: 'transparent',
														}}
													/>
												) : (
													<EmployeePicker
														style={{
															borderRadius: 24,
															color:
																'var(--primary-btn-color, var(--theme-primary))',
															fontSize: '14px',
															fontWeight: 700,
															border:
																'1px solid var(--primary-btn-color, var(--theme-primary))',
															width: '165px',
															background: 'transparent',
															height: '34px',
														}}
														allowMultiple={true}
														selectedEmployee={selectedEmployee}
														setSelectedEmployee={(value) => {
															setSelectedEmployee(value);
															setSendRewardObject({
																...sendRewardObject,
																employeeDetail: value,
															});
															setIsEmployeeSelected(false);
														}}
														recipients={recipients}
														setRecipients={setRecipients}
														setSearchedEmployeeData={setSearchedEmployeeData}
														placeholder={
															<div style={{ width: 120 }}>
																<RecipientIcon
																	style={{
																		height: 18,
																		width: 18,
																		marginRight: '4px',
																		display: 'inline-block',
																	}}
																/>
																<span style={{ verticalAlign: 'middle' }}>
																	Select Recipients
																</span>
															</div>
														}
													/>
												)}
											</>
										)}
										{isEmployeeSelected && !isEmployeeDetailValid ? (
											<div className="ant-form-explain text-danger mt-1">
												{allowRecognitionsForAccountClaims
													? 'Please add employee or account claim'
													: 'Please add employee'}
											</div>
										) : (
											<></>
										)}
									</div>
									{sendRewardObject?.selectedBadge ? (
										<div
											onMouseOver={() => handleMouseOver('reward')}
											onMouseLeave={handleMouseLeave}
											onClick={() => {
												setSendRewardObject({
													...sendRewardObject,
													selectedBadge: null,
													rewardtype: null,
												});
											}}
											style={
												isHovered === 'reward' ? { cursor: 'pointer' } : {}
											}
										>
											<div className="d-flex align-items-center badge-box">
												<div className="feed-reward-logo">
													<RewardIcon style={{ height: 18, width: 18 }} />
												</div>
												<div className="d-flex align-items-center text-box">
													<span className="selected-badge-name">
														{sendRewardObject?.selectedBadge?.name}{' '}
													</span>
													<CloseOutlined
														onClick={() => {
															setSendRewardObject({
																...sendRewardObject,
																selectedBadge: null,
																rewardtype: null,
															});
														}}
													/>
												</div>
											</div>
										</div>
									) : (
										<div>
											<Button
												shape="round"
												className={
													'd-flex justify-content-start add-reward-btn' +
													(!isEmployeeDetailValid
														? ' reward-unselected'
														: ' reward-selected')
												}
												disabled={!isEmployeeDetailValid}
												onClick={() => {
													setIsOpenRewardModel(true);
												}}
											>
												<RewardIcon style={{ height: 18, width: 18 }} />
												<span style={{ marginLeft: 0 }}>
													Select Recognition
												</span>
											</Button>
										</div>
									)}
								</div>
							</div>
							<TextAreaWithTags
								sendRewardObject={sendRewardObject}
								setSendRewardObject={setSendRewardObject}
								availableTags={availableTags}
								cursorPosition={cursorPosition}
								setCursorPosition={setCursorPosition}
								shouldFocusTextArea={shouldFocusTextArea}
								setShouldFocusTextArea={setShouldFocusTextArea}
							/>
							{sendRewardObject?.content?.gifURL && (
								<div className="selected-gif">
									<img
										src={sendRewardObject?.content?.gifURL}
										alt="Selected GIF"
									/>
									<div className="gif-close-btn">
										<DeleteOutlined onClick={handleDeleteGif} />
									</div>
								</div>
							)}
							<div className="d-flex align-items-center w-100 justify-content-between">
								<div className="d-flex">
									<div
										className="pr-2 gif-emogi"
										onClick={() => {
											setIsEmojiPickerVisible(false);
											setIsGifPickerVisible(!isGifPickerVisible);
										}}
									>
										<img src={gifIcon} alt="gifIcon" />
									</div>
									<div
										className="gif-emogi"
										onClick={(e) => {
											e.preventDefault();
											setIsGifPickerVisible(false);
											setIsEmojiPickerVisible(!isEmojiPickerVisible);
										}}
									>
										<img src={emoteIcon} alt="emoteIcon" />
									</div>
								</div>
								<div>
									{isShoutOutSend ? (
										<Spin className="d-flex align-items-center justify-content-center w-100 h-100" />
									) : (
										<Tooltip
											title={
												company?.recognitionSettings?.requireBadgeSelection &&
												isEmployeeDetailValid &&
												isContentValid &&
												!rewardtype
													? 'Please select a badge before sending your recognition.'
													: requireBadgeAssignmentMessage &&
														  isEmployeeDetailValid &&
														  rewardtype &&
														  (!content?.message ||
																content.message.trim().length === 0)
														? 'A text message is required for badge assignments.'
														: 'Please select a badge before sending your recognition.'
											}
											placement="topRight"
											open={
												(company?.recognitionSettings?.requireBadgeSelection &&
													isEmployeeDetailValid &&
													isContentValid &&
													!rewardtype) ||
												(requireBadgeAssignmentMessage &&
													isEmployeeDetailValid &&
													rewardtype &&
													(!content?.message ||
														content.message.trim().length === 0))
													? undefined
													: false
											}
										>
											<div className="send-primary-btn">
												<Button
													type="primary"
													onClick={() => {
														setIsGifPickerVisible(false);
														setIsEmojiPickerVisible(false);
														setSelectedEmployee(null);
														setRecipients([]);
														setAccountClaimId([]);
														setFullAccountClaim([]);
														handleSendShoutout();
													}}
													disabled={
														!(
															isEmployeeDetailValid &&
															(company?.recognitionSettings
																?.requireBadgeSelection
																? rewardtype
																: isContentValid || rewardtype) &&
															!(
																requireBadgeAssignmentMessage &&
																rewardtype &&
																(!content?.message ||
																	content.message.trim().length === 0)
															)
														)
													}
												>
													<span className="d-flex align-items-center">
														<span className="pr-2">Send </span>
														{!(isEmployeeDetailValid &&
														company?.recognitionSettings?.requireBadgeSelection
															? rewardtype
															: isContentValid || rewardtype) ? (
															<img
																src={send_gray}
																style={{ height: 18, width: 18 }}
															/>
														) : (
															<img
																src={send}
																style={{ height: 18, width: 18 }}
															/>
														)}
													</span>
												</Button>
											</div>
										</Tooltip>
									)}
								</div>
							</div>
							<div className="gif-emogi-modal mt-2">
								{isGifPickerVisible &&
									(gifIds ? (
										<CustomGifPicker
											gifIds={gifIds}
											onGifClick={handleOnGifClicked}
										/>
									) : (
										<GifPicker
											provider={klipyProvider}
											onGifClick={handleOnGifClicked}
										/>
									))}
								<EmojiPicker
									open={isEmojiPickerVisible}
									lazyLoadEmojis={true}
									hiddenEmojis={[
										'1f922',
										'1f92e',
										'1f621',
										'1f92c',
										'1f480',
										'2620-fe0f',
										'1f4a9',
										'1f4a3',
										'1f595',
										'1f44e',
										'1f44a',
										'1f6ac',
										'26b0-fe0f',
										'1faa6',
										'26b1-fe0f',
										'1f52b',
									]}
									onEmojiClick={handleOnEmojiClicked}
								/>
							</div>
						</div>
					</div>
					<Modal
						width={560}
						className=""
						open={isOpenRewardModel}
						title="Select Recognition"
						footer={false}
						onOk={false}
						onCancel={() => {
							setSendRewardObject({
								...sendRewardObject,
								rewardtype: null,
								selectedBadge: null,
							});
							setIsOpenRewardModel(false);
						}}
						destroyOnClose={true}
					>
						<SendRewardContainer
							checkAllEligibility={checkAllEligibility}
							setIsOpenRewardModel={setIsOpenRewardModel}
							sendRewardObject={sendRewardObject}
							setSendRewardObject={setSendRewardObject}
						/>
					</Modal>

					<Modal
						width={420}
						open={isCustomModalVisible}
						title={customModal?.title || 'Recognition'}
						footer={null}
						onCancel={handleCustomModalClose}
						destroyOnClose={true}
						centered
					>
						<div
							dangerouslySetInnerHTML={{
								__html: sanitizer(customModal?.content || '', {
									ADD_ATTR: ['target', 'rel'],
								}),
							}}
						/>
					</Modal>

					<RewardsProgressContainer />
					<RewardPointsAndBonusContainer
						recognitionPointsTotal={recognitionPointsTotal}
						isLoading={userStatsLoading}
					/>
					<RecentActivityContainer
						badgeList={badgeList}
						assignmentsSent={assignmentsSent}
						isLoading={isLoading}
						error={error}
					/>
				</div>
				<div className="card-scroll feed-container">
					<div style={{ height: '100%' }}>
						<Feed
							handleReplySubmit={handleReplySubmit}
							handleSetToPrivate={handleSetToPrivate}
							handleClapClick={handleClapClick}
							newestFeedData={newestFeedData}
							topFeedData={topFeedData}
							teamFeedData={teamFeedData}
							milestonesFeedData={milestonesFeedData}
							newestFeedLoading={newestFeedLoading}
							topFeedLoading={topFeedLoading}
							teamFeedLoading={teamFeedLoading}
							milestonesFeedLoading={milestonesFeedLoading}
							hasMoreNewestFeedData={hasMoreNewestFeedData}
							hasMoreTopFeedData={hasMoreTopFeedData}
							hasMoreTeamFeedData={hasMoreTeamFeedData}
							hasMoreMilestonesFeedData={hasMoreMilestonesFeedData}
							isLoadingMoreNewestFeed={isLoadingMoreNewestFeed}
							isLoadingMoreTopFeed={isLoadingMoreTopFeed}
							isLoadingMoreTeamFeed={isLoadingMoreTeamFeed}
							isLoadingMoreMilestonesFeed={isLoadingMoreMilestonesFeed}
							loadMoreNewestFeed={loadMoreNewestFeed}
							loadMoreTopFeed={loadMoreTopFeed}
							loadMoreTeamFeed={loadMoreTeamFeed}
							loadMoreMilestonesFeed={loadMoreMilestonesFeed}
							tagsFeed={tagsFeed}
							selectedTags={selectedTags}
							setSelectedTags={setSelectedTags}
						/>
					</div>
				</div>
				<div className="card-scroll ">
					<RecognitionAnnouncements currentUser={currentUser} />
					<RewardDashboardContainer
						badgeList={badgeList}
						isLoading={isLoading}
						error={error}
						isSharingVisible={isSharingVisible}
						currentUser={currentUser}
					/>
					{!company?.recognitionSettings?.hideRecognitionLeaderboard && (
						<RewardsLeaderBoardContainer
							recognitionPointsLeaderboard={recognitionPointsLeaderboard}
							recognitionPointsBySubCompanyLeaderboard={
								recognitionPointsBySubCompanyLeaderboard
							}
							isLoading={userStatsLoading}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default DashboardPresentation;
